import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDashboardStats, getDashboardClients, getDashboardTypes, getProjects } from '../api/client';

// ── Constants ──────────────────────────────────────────────────────────────────
function today() {
  return new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}
function formatDue(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}
function isOverdue(dateStr) {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date();
}
function progressForStatus(status) {
  return { pending: 10, active: 10, assessing: 45, qa: 80, complete: 100 }[status] || 10;
}

const STAGE_COLORS = {
  pending:   '#f5a623',
  assessing: '#3a8ac4',
  qa:        '#9b59b6',
  complete:  '#2ec98b',
};

// Fixed colors per obstacle type — shared constant so badges elsewhere can reuse
const TYPE_PALETTE = [
  '#3a8ac4', '#f5a623', '#2ec98b', '#e84040', '#9b59b6',
  '#16a085', '#e67e22', '#1abc9c', '#8e44ad', '#c0392b',
];

// ── MetricCard ─────────────────────────────────────────────────────────────────
function MetricCard({ value, label, sub, accent, accentColor }) {
  return (
    <div className="dg-kpi" style={{ flex: 1 }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10, marginBottom: 14,
        background: accent ? 'rgba(198,167,68,0.18)' : 'var(--blue-glass-fill)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: accentColor || (accent ? '#C6A744' : '#5F98C4'), fontSize: 18,
      }}>
        {accent ? '◆' : '●'}
      </div>
      <div style={{
        fontSize: 40, fontWeight: 700, lineHeight: 1, letterSpacing: '-1.5px',
        color: accentColor || (accent ? '#C6A744' : 'var(--text-primary)'),
      }}>
        {value}
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 8 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// ── Stage Bar Chart (SVG, no library) ─────────────────────────────────────────
function StageBarChart({ data, onBarClick }) {
  const W = 480, H = 160, PAD = { top: 10, right: 16, bottom: 36, left: 32 };
  const inner_w = W - PAD.left - PAD.right;
  const inner_h = H - PAD.top - PAD.bottom;
  const max = Math.max(...data.map(d => d.count), 1);
  const barW = inner_w / data.length;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', overflow: 'visible' }}>
      {/* Y axis ticks */}
      {[0, Math.ceil(max / 2), max].map(v => {
        const y = PAD.top + inner_h - (v / max) * inner_h;
        return (
          <g key={v}>
            <line x1={PAD.left} x2={PAD.left + inner_w} y1={y} y2={y}
              stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
            <text x={PAD.left - 6} y={y + 4} textAnchor="end" fill="var(--text-tertiary)" fontSize="9">{v}</text>
          </g>
        );
      })}

      {data.map((d, i) => {
        const barH = Math.max((d.count / max) * inner_h, d.count > 0 ? 2 : 0);
        const x = PAD.left + i * barW + barW * 0.15;
        const w = barW * 0.7;
        const y = PAD.top + inner_h - barH;
        const color = STAGE_COLORS[d.stage] || '#5F98C4';
        return (
          <g key={d.stage} onClick={() => onBarClick?.(d.stage)} style={{ cursor: onBarClick ? 'pointer' : 'default' }}>
            <rect x={x} y={y} width={w} height={barH}
              rx="3" fill={color} opacity="0.75"
              style={{ transition: 'opacity 0.15s' }}
              onMouseEnter={e => e.target.style.opacity = 1}
              onMouseLeave={e => e.target.style.opacity = 0.75}
            />
            <text x={x + w / 2} y={PAD.top + inner_h + 14} textAnchor="middle"
              fill="var(--text-tertiary)" fontSize="10">{d.label}</text>
            {d.count > 0 && (
              <text x={x + w / 2} y={y - 4} textAnchor="middle" fill={color} fontSize="11" fontWeight="700">
                {d.count}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// ── Type Pie Chart (SVG) ───────────────────────────────────────────────────────
function TypePieChart({ data }) {
  const R = 70, CX = 90, CY = 90;
  let cumAngle = -Math.PI / 2;

  function slice(pct) {
    const angle = (pct / 100) * 2 * Math.PI;
    const x1 = CX + R * Math.cos(cumAngle);
    const y1 = CY + R * Math.sin(cumAngle);
    cumAngle += angle;
    const x2 = CX + R * Math.cos(cumAngle);
    const y2 = CY + R * Math.sin(cumAngle);
    const large = angle > Math.PI ? 1 : 0;
    return `M ${CX} ${CY} L ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2} Z`;
  }

  // Single item — full circle
  if (data.length === 1) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
        <svg viewBox="0 0 180 180" style={{ width: 130, flexShrink: 0 }}>
          <circle cx={CX} cy={CY} r={R} fill={TYPE_PALETTE[0]} opacity="0.8" />
          <text x={CX} y={CY + 5} textAnchor="middle" fill="#fff" fontSize="14" fontWeight="700">100%</text>
        </svg>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{data[0].type}</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
      <svg viewBox="0 0 180 180" style={{ width: 130, flexShrink: 0 }}>
        {data.map((d, i) => (
          <path key={d.type} d={slice(d.pct)} fill={TYPE_PALETTE[i % TYPE_PALETTE.length]} opacity="0.85">
            <title>{d.type}: {d.count} ({d.pct}%)</title>
          </path>
        ))}
        <circle cx={CX} cy={CY} r={R * 0.48} fill="var(--t-bg1, #0f1e35)" />
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
        {data.map((d, i) => (
          <div key={d.type} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, flexShrink: 0, background: TYPE_PALETTE[i % TYPE_PALETTE.length] }} />
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.type}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-primary)' }}>{d.pct}%</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Overdue Aging Chart ───────────────────────────────────────────────────────
function OverdueAgingChart({ data }) {
  const max = Math.max(...data.map(d => d.count), 1);
  const COLORS = ['rgba(245,166,35,0.7)', 'rgba(232,100,30,0.8)', 'rgba(232,64,64,0.9)'];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {data.map((d, i) => (
        <div key={d.bucket} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 70, fontSize: 11, color: 'var(--text-tertiary)', flexShrink: 0, textAlign: 'right' }}>{d.bucket}</div>
          <div style={{ flex: 1, height: 24, background: 'rgba(255,255,255,0.05)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${(d.count / max) * 100}%`,
              background: COLORS[i], borderRadius: 4,
              transition: 'width 0.4s ease', minWidth: d.count > 0 ? 4 : 0,
            }} />
          </div>
          <div style={{ width: 24, fontSize: 12, fontWeight: 700, color: COLORS[i], flexShrink: 0 }}>{d.count}</div>
        </div>
      ))}
    </div>
  );
}

// ── Project Calendar ──────────────────────────────────────────────────────────
function ProjectCalendar({ projects, navigate }) {
  const now = new Date();
  const [cur, setCur] = useState(new Date(now.getFullYear(), now.getMonth(), 1));
  const year = cur.getFullYear(), month = cur.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const statusColor = s => STAGE_COLORS[s] || STAGE_COLORS.pending;
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  return (
    <div className="dg-panel">
      <div style={{ padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--glass-border)' }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>Project Deadlines</div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button onClick={() => setCur(new Date(year, month - 1, 1))}
            style={{ width: 30, height: 30, borderRadius: 8, padding: 0, cursor: 'pointer', background: 'var(--glass-fill)', border: '1px solid var(--glass-border)', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', minWidth: 130, textAlign: 'center' }}>
            {cur.toLocaleString('default', { month: 'long', year: 'numeric' })}
          </span>
          <button onClick={() => setCur(new Date(year, month + 1, 1))}
            style={{ width: 30, height: 30, borderRadius: 8, padding: 0, cursor: 'pointer', background: 'var(--glass-fill)', border: '1px solid var(--glass-border)', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6" /></svg>
          </button>
        </div>
      </div>
      <div style={{ padding: '16px 20px 20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 6 }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
            <div key={d} style={{ textAlign: 'center', fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-tertiary)', padding: '4px 0' }}>{d}</div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
          {cells.map((date, i) => {
            if (!date) return <div key={i} />;
            const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
            const isToday = dateStr === todayStr;
            const dayProjects = projects.filter(p => p.due_date === dateStr);
            return (
              <div key={i} style={{ background: isToday ? 'rgba(44,114,174,0.15)' : 'rgba(255,255,255,0.02)', border: `1px solid ${isToday ? 'rgba(95,152,196,0.4)' : 'rgba(255,255,255,0.05)'}`, borderRadius: 8, minHeight: 72, padding: 5, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <div style={{ fontSize: 11, fontWeight: isToday ? 700 : 500, color: isToday ? '#5F98C4' : 'var(--text-tertiary)', textAlign: 'right' }}>{date.getDate()}</div>
                {dayProjects.map(p => (
                  <div key={p.id} onClick={() => navigate(`/projects/${p.id}`)}
                    style={{ fontSize: 9, borderRadius: 4, padding: '2px 5px', background: statusColor(p.status) + '22', color: statusColor(p.status), border: `1px solid ${statusColor(p.status)}44`, cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={p.name}>{p.name}</div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
const FILTER_KEY = 'tsela-dashboard-filters';

export default function Dashboard() {
  const navigate = useNavigate();

  // Restore last-used filters from localStorage (spec §6)
  const savedFilters = (() => { try { return JSON.parse(localStorage.getItem(FILTER_KEY) || '{}'); } catch { return {}; } })();
  const [clientFilter, setClientFilter] = useState(savedFilters.client || '');
  const [typeFilter,   setTypeFilter]   = useState(savedFilters.type   || '');

  const [stats,   setStats]   = useState(null);
  const [clients, setClients] = useState([]);
  const [types,   setTypes]   = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);

  // Persist filters on change
  useEffect(() => {
    try { localStorage.setItem(FILTER_KEY, JSON.stringify({ client: clientFilter, type: typeFilter })); } catch {}
  }, [clientFilter, typeFilter]);

  // Load filter dropdown options + projects (once)
  useEffect(() => {
    Promise.all([
      import('../api/client').then(m => m.getDashboardClients()),
      import('../api/client').then(m => m.getDashboardTypes()),
      getProjects(),
    ]).then(([cRes, tRes, pRes]) => {
      setClients(cRes.data);
      setTypes(tRes.data);
      const list = pRes.data.projects || [];
      setProjects(list);
      setSelectedProject(list.find(p => p.project_type === 'annex_14_ols_assessment') || null);
    });
  }, []);

  // Re-fetch stats whenever filters change (spec §6 — all charts share same filter)
  const fetchStats = useCallback(() => {
    setLoading(true);
    const params = {};
    if (clientFilter) params.client_name = clientFilter;
    if (typeFilter)   params.obstacle_type = typeFilter;
    import('../api/client').then(m => m.getDashboardStats(params))
      .then(r => setStats(r.data))
      .finally(() => setLoading(false));
  }, [clientFilter, typeFilter]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const selectSx = {
    background: 'var(--glass-fill)', border: '1px solid var(--glass-border)',
    borderRadius: 6, padding: '7px 12px', color: 'var(--text-primary)',
    fontSize: 12, outline: 'none', cursor: 'pointer',
  };

  const hasOverdue = stats?.summary?.overdue > 0;
  const activeForCalendar = projects.filter(p => p.status !== 'complete');

  return (
    <div style={{ padding: 32, overflowY: 'auto', height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 22 }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.5px' }}>Operational Overview</div>
          <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 4, fontWeight: 300 }}>{today()}</div>
        </div>
      </div>

      {/* ── Filters (spec §6) ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <select value={clientFilter} onChange={e => setClientFilter(e.target.value)} style={selectSx}>
          <option value="">All Clients</option>
          {clients.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={selectSx}>
          <option value="">All Types</option>
          {types.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        {(clientFilter || typeFilter) && (
          <button onClick={() => { setClientFilter(''); setTypeFilter(''); }}
            style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}>
            Clear filters
          </button>
        )}
        {loading && <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Refreshing…</span>}
      </div>

      {/* ── Metric Cards (spec §4) ── */}
      <div style={{ display: 'flex', gap: 14 }}>
        <MetricCard value={stats?.summary?.active_projects ?? '—'}  label="Active Projects"   sub={`${stats?.summary?.in_progress ?? 0} in progress`} accent />
        <MetricCard value={stats?.summary?.in_progress ?? '—'}      label="Under Assessment"  accentColor="#3a8ac4" />
        <MetricCard value={stats?.summary?.overdue ?? '—'}           label="Overdue"           accentColor={hasOverdue ? '#e84040' : undefined} />
        <MetricCard value={stats?.summary?.completed_last_30d ?? '—'} label="Completed (30d)" accentColor="#2ec98b" />
      </div>

      {/* ── Charts row (spec §4) ── */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 16 }}>

          {/* Stage bar chart — ~60% */}
          <div className="dg-panel">
            <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--glass-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>Projects by Stage</div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Click a bar to view projects</div>
            </div>
            <div style={{ padding: '20px 24px 12px' }}>
              <StageBarChart
                data={stats.by_stage}
                onBarClick={stage => navigate(`/projects?status=${stage}`)}
              />
            </div>
          </div>

          {/* Type pie chart — ~40% */}
          <div className="dg-panel">
            <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--glass-border)' }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>Projects by Type</div>
            </div>
            <div style={{ padding: '24px' }}>
              {stats.by_type.length === 0 ? (
                <div style={{ color: 'var(--text-tertiary)', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No type data</div>
              ) : (
                <TypePieChart data={stats.by_type} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Overdue aging chart — full width, conditional (spec §5) ── */}
      {stats && hasOverdue && (
        <div className="dg-panel">
          <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--glass-border)' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#e84040' }}>
              ⚠ Overdue Aging — {stats.summary.overdue} project{stats.summary.overdue !== 1 ? 's' : ''} past due
            </div>
          </div>
          <div style={{ padding: '20px 24px' }}>
            <OverdueAgingChart data={stats.overdue_aging} />
          </div>
        </div>
      )}

      {/* ── Active project list + OLS info ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 16, alignItems: 'start' }}>

        {/* Active Projects list */}
        <div className="dg-panel">
          <div style={{ padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--glass-border)' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>Active Projects</div>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{projects.filter(p => p.status !== 'complete').length} projects</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 130px 70px 36px', gap: 12, padding: '9px 24px', fontSize: 10, fontWeight: 600, letterSpacing: '1.2px', textTransform: 'uppercase', color: 'var(--text-tertiary)', borderBottom: '1px solid rgba(255,255,255,0.04)', background: 'rgba(255,255,255,0.015)' }}>
            <div>Project</div><div>Status</div><div>Progress</div><div>Due</div><div />
          </div>
          {projects.filter(p => p.status !== 'complete').slice(0, 10).map((p, i, arr) => {
            const pct = progressForStatus(p.status);
            const isGold = pct >= 75;
            const isSelected = selectedProject?.id === p.id;
            return (
              <div key={p.id} className="dg-row-hover"
                onClick={() => setSelectedProject(isSelected ? null : p)}
                style={{
                  display: 'grid', gridTemplateColumns: '1fr 110px 130px 70px 36px',
                  gap: 12, alignItems: 'center', padding: '14px 24px',
                  borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                  cursor: 'pointer',
                  background: isSelected ? 'rgba(44,114,174,0.10)' : 'transparent',
                  borderLeft: isSelected ? '3px solid #5F98C4' : '3px solid transparent',
                }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#5F98C4' }}>{p.airport_designator || p.name.split(' ')[0]}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{p.client_name ? `${p.client_name} — ` : ''}{p.name}</div>
                </div>
                <div>
                  <span className={`dg-chip ${p.status === 'active' ? 'pending' : p.status}`}>
                    {p.status === 'active' ? 'Pending' : p.status === 'assessing' ? 'Assessment' : p.status === 'qa' ? 'QA Review' : p.status}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{pct}%</div>
                  <div className="dg-bar-track">
                    <div className={`dg-bar-fill${isGold ? ' gold' : ''}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
                <div style={{ fontSize: 12, color: isOverdue(p.due_date) ? '#f5a623' : 'var(--text-tertiary)' }}>{formatDue(p.due_date)}</div>
                <div onClick={e => { e.stopPropagation(); navigate(`/projects/${p.id}`); }}
                  style={{ color: 'var(--text-tertiary)', fontSize: 15, textAlign: 'center', cursor: 'pointer' }} title="Open project">→</div>
              </div>
            );
          })}
          {projects.filter(p => p.status !== 'complete').length === 0 && (
            <div style={{ padding: '32px 24px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>No active projects</div>
          )}
        </div>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* OLS Report Info — only for OLS project_type */}
          {selectedProject?.project_type === 'annex_14_ols_assessment' && (
            <div className="dg-panel">
              <div style={{ padding: '18px 24px', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>OLS Report Information</div>
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>{selectedProject.name}</div>
                </div>
                <button onClick={() => navigate(`/projects/${selectedProject.id}/assess`)}
                  style={{ background: 'none', border: 'none', color: '#5F98C4', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  Open →
                </button>
              </div>
              {[
                { label: 'Airport Name',       value: selectedProject.airport_name },
                { label: 'Airport Designator', value: selectedProject.airport_designator },
                { label: 'Survey Company',     value: selectedProject.survey_company },
                { label: 'Survey Year',        value: selectedProject.survey_year },
              ].map((r, i, arr) => (
                <div key={r.label} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '11px 22px',
                  borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                }}>
                  <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{r.label}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: r.value ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>{r.value || '—'}</div>
                </div>
              ))}
            </div>
          )}

          {/* Quick Stats */}
          <div className="dg-panel">
            <div style={{ padding: '18px 24px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>Quick Stats</div>
            </div>
            {[
              { label: 'Total projects',    value: projects.length, cls: '' },
              { label: 'Total obstacles',   value: projects.reduce((a, p) => a + (p.obstacle_count || 0), 0), cls: '' },
              { label: 'Completion rate',   value: projects.length > 0 ? `${Math.round((projects.filter(p => p.status === 'complete').length / projects.length) * 100)}%` : '—', cls: 'blue' },
              { label: 'Overdue items',     value: stats?.summary?.overdue ?? '—', cls: (stats?.summary?.overdue || 0) > 0 ? 'gold' : '' },
            ].map((s, i, arr) => (
              <div key={s.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 22px', borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{s.label}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: s.cls === 'blue' ? '#5F98C4' : s.cls === 'gold' ? '#C6A744' : 'var(--text-primary)' }}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Data Manager shortcut */}
          <div className="dg-panel dg-row-hover" onClick={() => navigate('/data')}
            style={{ cursor: 'pointer', padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 38, height: 38, borderRadius: 11, background: 'var(--blue-glass-fill)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5F98C4', flexShrink: 0 }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                <ellipse cx="12" cy="5" rx="9" ry="3"/>
                <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
                <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
              </svg>
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Data Manager</div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>ARP · CNS · THR reference tables</div>
            </div>
            <div style={{ marginLeft: 'auto', color: 'var(--text-tertiary)', fontSize: 16 }}>→</div>
          </div>
        </div>
      </div>

      {/* ── Calendar ── */}
      <ProjectCalendar projects={activeForCalendar} navigate={navigate} />
    </div>
  );
}
